import React, { useEffect, useState } from 'react'
import { Captcha } from 'shared/components/captcha'
import { Config, FormValues } from 'donate/types'
import { connect } from 'formik'
import { useCallback } from 'react'

const CAPTCHA_SOLUTION_FIELD_NAME = 'captcha_solution'
const CAPTCHA_ERROR_FIELD_NAME = 'captcha_error'
const CAPTCHA_COMPLETE = '_captcha_complete'

export const DonationFormCaptcha = connect<{ config: Config }, FormValues>(
  ({ formik: { setFieldValue, validateForm, errors, isValid, isSubmitting }, config }) => {
    const [backendSubmitCount, setBackendSubmitCount] = useState<number>(0)
    const [backendErrorCount, setBackendErrorCount] = useState<number>(0)

    const onCaptchaSuccess = useCallback(
      (solution) => {
        void setFieldValue(CAPTCHA_COMPLETE, true)
        void setFieldValue(CAPTCHA_SOLUTION_FIELD_NAME, solution)
      },
      [setFieldValue]
    )

    const onCaptchaError = useCallback(
      (error, value) => {
        void setFieldValue(CAPTCHA_COMPLETE, true)
        void setFieldValue(CAPTCHA_ERROR_FIELD_NAME, value)
        console.error(error)
      },
      [setFieldValue]
    )

    const onCaptchaReset = useCallback(() => {
      // this causes a problem, because it invalidates the form again
      // and therefore replaces the backend error
      // therefore we cannot have two different error messages
      // until we rebuild the whole form error handling
      void setFieldValue(CAPTCHA_COMPLETE, false)
    }, [setFieldValue])

    useEffect(() => {
      if (isValid && isSubmitting) {
        setBackendSubmitCount(backendSubmitCount + 1)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid, isSubmitting])

    useEffect(() => {
      // We want to reset the Captcha when we get an error from the backend,
      // because we don't want the user (bot) to be able to simply re-submit the form
      // and friendly captcha would complain about a duplicate solution.
      // However, in all other cases, we don't want to add more hassle for the user than necessary.

      // we only want to know about the backend errors so we have to filter out the captcha validation error
      const { _captcha_complete, ...errorsWithoutCaptchaComplete } = errors
      if (backendSubmitCount > 0 && Object.keys(errorsWithoutCaptchaComplete).length) {
        setBackendErrorCount(backendErrorCount + 1)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [backendSubmitCount, errors])

    const useFakeCaptcha = () => {
      useEffect(() => {
        void setFieldValue(CAPTCHA_SOLUTION_FIELD_NAME, 'test')
        void setFieldValue(CAPTCHA_COMPLETE, true)
        setTimeout(() => void validateForm(), 300)
      }, [])
    }

    if (document.body.classList.contains('env-test')) {
      useFakeCaptcha()
      return null
    }

    return (
      <>
        <Captcha
          siteKey={config.captchaSiteKey}
          endpoint={config.captchaEndpoint}
          onSuccess={onCaptchaSuccess}
          onError={onCaptchaError}
          onReset={onCaptchaReset}
          resetTrigger={backendErrorCount}
          fieldName={CAPTCHA_SOLUTION_FIELD_NAME}
        />
      </>
    )
  }
)
